import { StateCreator } from "zustand";
import { jwtDecode } from "jwt-decode";
import { RolePermissions } from "@/common/config/defaults";
import { DecodedToken } from "@/common/types/types";
import { queryClient } from "@/api-services/config/QueryProvider";

export type AuthSlice = {
  auth: {
    state: {
      isAuthenticated: boolean;
      accessToken: string | null;
      refreshToken: string | null;
      user: {
        userId: string;
        groupIds: string[];
        orgId?: string;
        username: string;
        email: string;
        role: string;
      } | null;
      role: keyof typeof RolePermissions | null;
      exp: number | null;
    };
    isLoggingOut: boolean;
    restoreAuth: () => void;
    login: (
      accessToken: string,
      refreshToken: string,
      userData: {
        username: string;
        email: string;
        role: string;
      },
    ) => void;
    logout: () => void;
    hasPermission: (permission: string) => boolean;
    initialized: boolean;
    refreshSession: (
      accessToken: string,
      userData: {
        username: string;
        email: string;
        role: string;
      },
      refreshToken?: string,
    ) => void; // Add this function
  };
};

const initialAuthState = {
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
  user: null,
  role: null,
  exp: null,
};

export const createAuthSlice: StateCreator<AuthSlice> = (set, get) => ({
  auth: {
    state: initialAuthState,
    initialized: false,
    isLoggingOut: false,
    login: (
      accessToken: string,
      refreshToken: string,
      userData: {
        username: string;
        email: string;
        role: string;
      },
    ) => {
      const decoded = jwtDecode<DecodedToken>(accessToken);
      // const testExp = Math.floor(Date.now() / 1000) + 190;
      const newState = {
        isAuthenticated: true,
        accessToken,
        refreshToken,
        user: {
          userId: decoded.userId,
          groupIds: decoded.groupIds,
          ...userData,
          ...(decoded.orgId && { orgId: decoded.orgId }),
        },
        role: decoded.role,
        exp: decoded.exp,
        initialized: true,
      };
      localStorage.setItem(
        "auth",
        JSON.stringify({ accessToken, refreshToken, userData }),
      );
      set((state) => ({
        auth: { ...state.auth, isLoggingOut: false, state: newState },
      }));
    },

    restoreAuth: () => {
      const storedAuth = localStorage.getItem("auth");

      if (!storedAuth) {
        set((state) => ({
          auth: {
            ...state.auth,
            state: { ...state.auth.state },
            initialized: true,
          },
        }));
        return;
      }

      try {
        const { accessToken, refreshToken, userData } = JSON.parse(storedAuth);
        const decoded = jwtDecode<DecodedToken>(accessToken);

        if (decoded.exp * 1000 <= Date.now()) {
          get().auth.logout();
          return;
        }

        set((state) => ({
          auth: {
            ...state.auth,
            isLoggingOut: false,
            state: {
              isAuthenticated: true,
              accessToken,
              refreshToken,
              user: {
                userId: decoded.userId,
                groupIds: decoded.groupIds,
                ...userData,
                ...(decoded.orgId && { orgId: decoded.orgId }),
              },
              role: decoded.role,
              exp: decoded.exp,
            },
            initialized: true,
          },
        }));
      } catch {
        get().auth.logout();
      }
    },

    logout: () => {
      localStorage.removeItem("auth");
      // Clear all React Query cache to prevent data leakage between users
      queryClient.clear();
      set((state) => ({
        auth: {
          ...state.auth,
          isLoggingOut: true,
          state: { ...initialAuthState },
          initialized: true,
        },
      }));
    },

    hasPermission: (permission: string) => {
      const role = get().auth.state.role;
      if (!role) return false;
      return RolePermissions[role]?.includes(permission);
    },

    /**
     * Refresh session using the refresh token.
     */
    refreshSession: (
      accessToken: string,
      userData: {
        username: string;
        email: string;
        role: string;
      },
      refreshToken?: string,
    ) => {
      const decoded = jwtDecode<DecodedToken>(accessToken);

      const updatedRefreshToken = refreshToken ?? get().auth.state.refreshToken;
      // const testExp = Math.floor(Date.now() / 1000) + 190;
      if (!updatedRefreshToken) {
        throw new Error("No refresh token available");
      }
      localStorage.setItem(
        "auth",
        JSON.stringify({
          accessToken,
          refreshToken: updatedRefreshToken,
          userData,
        }),
      );

      const newState = {
        isAuthenticated: true,
        accessToken,
        refreshToken: updatedRefreshToken,
        user: {
          userId: decoded.userId,
          groupIds: decoded.groupIds,
          ...userData,
          ...(decoded.orgId && { orgId: decoded.orgId }),
        },
        role: decoded.role,
        exp: decoded.exp,
      };

      set((state) => ({
        auth: {
          ...state.auth,
          state: newState,
        },
      }));
    },
  },
});
